"use client";

import Image from "next/image";
import { useCallback, useState } from "react";
import HorizontalSectionSeparator from "@/shared/components/ui/HorizontalSectionSeparator";
import dynamic from "next/dynamic";
import { logger } from "@/shared/lib/logger";
import { useFirmLogo } from "@/shared/hooks/useFirmLogo";
import { useSearchParams } from "next/navigation";
import msLogo from "@/shared/assets/logos/mswLogo.svg";

const OktaSignInWidget = dynamic(() => import("./OktaSignInWidget"), {
  ssr: false,
});

import { handleTokenPostLogin } from "@/shared/lib/auth/tokenStorage";
import { IS_MSW_PROGRAM } from "@/shared/lib/constant";
import { SocialSignIn } from "./SocialSignIn";

export default function LoginForm({
  showSignupLink,
}: {
  showSignupLink?: boolean;
}): React.JSX.Element {
  // Component Utils --> START
  // Component Utils --> END
  const { firmLogo, handleLogoError } = useFirmLogo();
  const searchParams = useSearchParams();
  const isMSWProgram = IS_MSW_PROGRAM();

  // Social buttons + sign-up link show only on the widget's primary login
  // screen; hidden on MFA/2FA and other secondary screens.
  const [showSocial, setShowSocial] = useState(true);

  // Hooks --> START
  // Memoized so toggling `showSocial` doesn't change callback identities and
  // re-run the widget effect (which would recreate the widget and loop).
  const handleLoginRequest = useCallback(
    async (tokens: any): Promise<void> => {
      const res = await handleTokenPostLogin(tokens.accessToken?.accessToken);

      if (res.ok) {
        // Preserve chat parameter if it exists
        const chatParam = searchParams.get("chat");
        window.location.href = chatParam
          ? `/landing?chat=${chatParam}`
          : "/landing";
      } else {
        logger.error("Server failed to store tokens", {
          status: res.status,
          statusText: res.statusText,
        });
      }
    },
    [searchParams],
  );

  const handleError = useCallback(() => {}, []);

  const handleScreenChange = useCallback((controller: string | undefined) => {
    setShowSocial(controller === "primary-auth");
  }, []);
  // Hooks --> END

  return (
    <div className="h-[100%] flex items-center justify-center">
      <section className="w-[85%] max-w-[400px]">
        {isMSWProgram ? (
          <div className="flex justify-center items-center">
            <Image
              src={msLogo}
              alt="Morgan Stanley at Work Logo"
              height={30}
              width={200}
              priority
            />
          </div>
        ) : (
          <Image
            src={firmLogo}
            onError={handleLogoError}
            alt="TIFIN Give Master Logo"
            height={64}
            width={280}
            // Bound by a box, not by width alone: a 6:1 wordmark constrained
            // only by width renders half the height of a 3:1 one. Wide logos
            // now hit the width cap, squarer ones the height cap.
            className="mx-auto mt-[10px] object-contain max-w-[280px] max-h-[64px] h-auto w-auto"
            priority
          />
        )}

        {/* TODO: Derive  */}
        <HorizontalSectionSeparator />

        <section>
          {/* TODO: Replace this with Typography from Design System (applies for all Typography content) */}
          <h1 className="text-[24px] ml-[10px]">
            Let&apos;s get ready to give.
          </h1>

          <OktaSignInWidget
            onSuccess={handleLoginRequest}
            onError={handleError}
            onScreenChange={handleScreenChange}
          />
          {showSignupLink && showSocial && <SocialSignIn />}
          {showSignupLink && showSocial && (
            <p className="mt-4 text-center text-sm text-secondary-foreground">
              Don&apos;t have an account?{" "}
              <a href="/signup" className="text-brand-primary underline">
                Sign up
              </a>
            </p>
          )}
        </section>
      </section>
    </div>
  );
}
